<template>
    <div class="questionnaire-step">
        <div
            v-for="(stepContent, index) in contentConditional"
            :key="index"
            class="questionnaire-step-content-wrapper"
        >
            <QuestionnaireStepContentQuestions
                v-if="
                    stepContent.__typename ===
                    'QuestionnaireStepContentQuestions'
                "
                :data="stepContent"
                :step-index="stepIndex"
                :step-title="step.title"
            />

            <CmsBlocks
                v-else
                :data="stepContent.blocks"
                page-type="questionnaire"
            />
        </div>

        <ThButton
            v-if="step.help.length"
            class="help-button"
            color="var(--slate)"
            type="link"
            @click="openHelpModal"
        >
            Help me choose
        </ThButton>

        <transition name="modal">
            <Fixed
                v-if="isHelpModalVisible"
                @close="closeHelpModal"
            >
                <HelpModal
                    :blocks="step.help"
                    @close-help-modal="closeHelpModal"
                />
            </Fixed>
        </transition>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import slugify from 'slugify'

import type {
    QuestionnaireStepContentContentFragment,
    QuestionnaireStepContentQuestionsFragment,
    QuestionnaireStepFragment,
} from '@/graphql/api'
import QuestionnaireStepContentQuestions from '@/components/cms/component/questionnaire/question/QuestionnaireStepContentQuestions.vue'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import HelpModal from '@/components/cms/component/questionnaire/question/HelpModal.vue'
import Fixed from '@/components/layout/modal/Fixed.vue'
import CmsBlocks from '@/components/cms/CmsBlocks.vue'

type QuestionnaireStepContent =
    | QuestionnaireStepContentContentFragment
    | QuestionnaireStepContentQuestionsFragment

const props = defineProps<{
    step: QuestionnaireStepFragment
    stepIndex: number
    contentConditional: Array<QuestionnaireStepContent>
}>()

const router = useRouter()
const route = useRoute()

const questionHelpHash = ref(`#help-me-choose-${slugify(props.step.title)}`)

const isHelpModalVisible = computed(
    () => props.step.help.length && route.hash === questionHelpHash.value
)

const openHelpModal = () =>
    router.push({
        ...route,
        hash: questionHelpHash.value,
    })
const closeHelpModal = router.back
</script>

<style lang="scss" scoped>
.questionnaire-step {
    width: 100%;
    max-width: 1440px;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0 auto;

    .questionnaire-step-content-wrapper {
        width: 100%;
    }

    .help-button {
        display: block;
        margin: 48px auto 120px;
    }
}

.modal-enter-active {
    transition: transform 0.24s var(--timing-function-easing-emphasized);
}

.modal-leave-active {
    transition: transform 0.18s var(--timing-function-easing-emphasized-reverse);
}

.modal-enter-from,
.modal-leave-to {
    transform: translateY(100vh);
}
</style>
